import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { GroupedList as FluentUIGroupedList, HighContrastSelector, IGroup, IGroupedListProps, IGroupHeaderProps, IGroupHeaderStyles, SelectionMode } from "@fluentui/react";
import React, { useEffect, useRef } from "react";
import { useComponentLogger } from "../../../shared/logging";
import { ComponentValidator, validateComponent } from "../../../shared/validation";
import { warnDeprecation } from "../../../shared/warnDeprecation";
import { ListWrapper } from "../ListWrapper/ListWrapper";
import { GroupedListItemBaseProps } from "../shared/props";
import { childrenAsElementArray, listItemDefaultBackgroundColor, onRenderCell } from "../shared/utils";
import { GroupedListInternalProps, GroupedListItemType, GroupedListProps } from "./GroupedList.types";

const isValidGroupItems = (groupItems: readonly JSX.Element[]): Boolean => {
    let prevListItemType: GroupedListItemType;
    for (let i = 0, n = groupItems.length; i < n; i++) {
        const listItem = groupItems[i];
        if (!React.isValidElement<GroupedListItemBaseProps>(listItem) || !listItem.props.children) {
            if (prevListItemType && prevListItemType !== "leaf") {
                return false;
            }
            prevListItemType = "leaf";
            continue;
        }
        if (prevListItemType && prevListItemType !== "non-leaf") {
            return false;
        }
        prevListItemType = "non-leaf";
        const subGroupItems = childrenAsElementArray(listItem.props.children);
        if (!isValidGroupItems(subGroupItems)) {
            return false;
        }
    }

    return true;
};

const groupedListValidator = (groupItems: readonly JSX.Element[]): ComponentValidator => (
    () => (!isValidGroupItems(groupItems) ? "Cannot mix childless and child-containing list nodes on the same level." : undefined)
);
// Creates style for headers. Without this, the entire header row gets a grey background and has selector states
const getGroupedListHeaderStyles = (theme: Theme, showDivider?: boolean): Partial<IGroupHeaderStyles> => ({
    root: {
        backgroundColor: theme.palette.neutralHigh,
        borderBottom: showDivider ? undefined : "0px",
        selectors: {
            ":focus": {
                background: theme.palette.neutralHigh,
            },
            ":hover": {
                background: theme.palette.neutralHigh,
            },
            ":active": {
                background: theme.palette.neutralHigh,
            },
            ":disabled": {
                background: theme.palette.neutralHigh,
            },
            [HighContrastSelector]: {
                border: "none"
            }
        }
    },
    groupHeaderContainer: {
        height: "100%"
    }
});

/** @deprecated Please use the List component with Drawer children instead. */
export const GroupedList: React.FC<GroupedListProps> = (props) => {
    useComponentLogger("GroupedList", props.id);

    return (
        <ListWrapper {...props} listType="GroupedList" />
    );
};

export const GroupedListInternal: React.FC<GroupedListInternalProps> = ({
    children,
    listItemBackgroundColor = listItemDefaultBackgroundColor,
    disabled,
    showDivider = true,
    expandButtonProps,
    ...otherProps
}) => {
    useEffect(() => {
        warnDeprecation("GroupedList has been deprecated, please use the new ListItem/MobileListItem with ListItemColumns instead.");
    }, []);

    validateComponent("GroupedList", [groupedListValidator(childrenAsElementArray(children))]);

    const { current: headerCollapsedStates } = useRef(new Map<string, boolean>());
    const theme = useTheme();

    const items: JSX.Element[] = [];
    const groups: ListOfGroups = createGroupsAndItems(childrenAsElementArray(children), headerCollapsedStates, items) || [];

    /**
     * a "shim" function, designed to match Fluent UI's expected render method
     * as we don't care about nestingDepth for now
     */
    const onRenderGroupedCell = (nestingDepth?: number, listItem?: JSX.Element) =>
        onRenderCell(listItem!, listItemBackgroundColor, disabled, nestingDepth!, showDivider);

    /**
     * Callback to render headers in the GroupedList.
     */
    const onRenderHeaderTitle = (headerProps?: IGroupHeaderProps) => {
        const groupHeader = headerProps!.group!.data as JSX.Element;

        // set `showDivider` to false as Fluent calls both `onRenderCell` and
        // headerProps.onRenderTitle, so if we have it set to true here, we're double-adding
        // `showDivider`
        return onRenderCell(groupHeader, listItemBackgroundColor, disabled, undefined, false);
    };

    const onRenderHeaderTitleUpdating = (headerProps?: IGroupHeaderProps) => {
        const groupHeader = headerProps!.group!.data as JSX.Element;

        return React.cloneElement(groupHeader, { menuDisabled: true });
    };

    const onToggleCollapse = (group: IGroup) => {
        // Save the toggle state of the header. Because FluentUI calls this function
        // before toggling the isCollapsed value, we need to NOT it.
        headerCollapsedStates.set(group.key, !group.isCollapsed);
    };

    const fluentOnShouldVirtualize = () => false;

    return (
        <FluentUIGroupedList
            groups={groups}
            items={items}
            onRenderCell={onRenderGroupedCell}
            groupProps={{
                headerProps: {
                    onRenderTitle: disabled ? onRenderHeaderTitleUpdating : onRenderHeaderTitle,
                    styles: getGroupedListHeaderStyles(theme, showDivider),
                    onToggleCollapse: onToggleCollapse,
                    expandButtonProps
                },
                role: "group"
            }}
            selectionMode={SelectionMode.none}
            onShouldVirtualize={fluentOnShouldVirtualize}
            rootListProps={otherProps}
        />
    );
};

type ListOfGroups = IGroupedListProps["groups"];

/**
 * @param groupItems the current subset of the "tree" of groups / items we're searching
 * @param headerCollapseStates Map of the current collapse state (for re-renders)
 * @param mutableItems Array of items to fill
 * @param level the level of nesting we are currently at
 * @param baseKey a unique string to differentiate groups
 */
const createGroupsAndItems = (
    groupItems: readonly JSX.Element[],
    headerCollapseStates: ReadonlyMap<string, boolean>,
    mutableItems: JSX.Element[] = [],
    level: number = 0,
    baseKey: string = "",
): ListOfGroups => {
    const groups: ListOfGroups = [];

    for (let i = 0, n = groupItems.length; i < n; i++) {
        const listItem = groupItems[i];

        // Checks whether the listitem is a "leaf node", which means it has no children and is purely a list item and
        // not a group itself. If we pass this check, then the listItem must be a group itself and must contain children
        if (!React.isValidElement<GroupedListItemBaseProps>(listItem) || !listItem.props.children) {
            mutableItems.push(listItem);
            continue;
        }

        let key = (listItem.key || i).toString();
        if (baseKey) {
            key = `${baseKey}-${i}`;
        }

        const subGroupItems = childrenAsElementArray(listItem.props.children);
        const startIndex = mutableItems.length;
        const count = subGroupItems.length;
        const children = createGroupsAndItems(subGroupItems, headerCollapseStates, mutableItems, level + 1, key);

        groups.push({
            count,
            key,
            name: listItem.props["aria-label"]!,
            ariaLabel: listItem.props["aria-label"]!,
            startIndex,
            level,
            children,
            isCollapsed: headerCollapseStates.has(key) ? headerCollapseStates.get(key) : !listItem.props.defaultExpanded,
            // Save the group header element, removing our custom props and children, and setting the computed key
            data: React.cloneElement(listItem, { defaultExpanded: undefined, children: undefined, key })
        });
    }

    return groups;
};
